'use client'

import Image from 'next/image'
import { motion } from 'framer-motion'
import React from 'react'
import type { LucideIcon } from 'lucide-react'

interface AllServicesSubSectionProps {
  title: string
  para1: string
  para2: string
  icon: LucideIcon
  imageSrc?: string
  imageAlt?: string
}

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

const AllServicesSubSection: React.FC<AllServicesSubSectionProps> = ({
  title,
  para1,
  para2,
  icon: Icon,
  imageSrc = '/medication-management.png',
  imageAlt = title,
}) => {
  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Text column */}
          <motion.div
            className="w-full lg:w-1/2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeIn}
          >
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 shrink-0">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 leading-tight">
                {title}
              </h2>
            </div>
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              {para1}
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              {para2}
            </p>
          </motion.div>

          {/* Image column */}
          <motion.div
            className="w-full lg:w-1/2 relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeIn}
          >
            <div className="relative h-64 sm:h-80 lg:h-96 w-full rounded-2xl overflow-hidden shadow-xl">
              <Image
                src={imageSrc}
                alt={imageAlt}
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-600/20 to-transparent" />
            </div>

            {/* Decorative blobs – hidden on mobile to prevent horizontal overflow */}
            <div className="hidden sm:block absolute -bottom-6 -left-6 w-32 h-32 lg:w-48 lg:h-48 bg-emerald-100 rounded-full opacity-70 -z-10" />
            <div className="hidden sm:block absolute -top-6 -right-6 w-24 h-24 lg:w-32 lg:h-32 bg-teal-100 rounded-full opacity-70 -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default AllServicesSubSection
